// AIJobsPanel — hiển thị danh sách AI Job (queue + history) cho business.
// Poll AIJobQueue mỗi 5s. Render status badge + meta + retry count.
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import AntAPI from '../../utils/AntAPI';
import AntLocalization from '../../utils/AntLocalization';
import './AIJobsPanel.scss';

// BE dùng song song 2 quy ước: Pending/Processing/Completed (worker enqueue) và
// Running/Done (tracking AIVoucherAnalysis). Map cả hai để hiển thị nhất quán.
const STATUS_META = {
    Pending:     { color: '#a0aec0', label: { vi: 'Chờ',      en: 'Queued', fr: 'En file', es: 'En cola', id: 'Antre', ja: '待機', zh: '排队', ko: '대기', hi: 'कतार में', ar: 'بالانتظار' } },
    Queued:      { color: '#a0aec0', label: { vi: 'Chờ',      en: 'Queued', fr: 'En file', es: 'En cola', id: 'Antre', ja: '待機', zh: '排队', ko: '대기', hi: 'कतार में', ar: 'بالانتظار' } },
    Processing:  { color: '#4299e1', label: { vi: 'Đang chạy', en: 'Running', fr: 'En cours', es: 'En curso', id: 'Berjalan', ja: '実行中', zh: '运行中', ko: '실행 중', hi: 'चल रहा है', ar: 'قيد التشغيل' } },
    Running:     { color: '#4299e1', label: { vi: 'Đang chạy', en: 'Running', fr: 'En cours', es: 'En curso', id: 'Berjalan', ja: '実行中', zh: '运行中', ko: '실행 중', hi: 'चल रहा है', ar: 'قيد التشغيل' } },
    Completed:   { color: '#48bb78', label: { vi: 'Xong',     en: 'Done', fr: 'Terminé', es: 'Listo', id: 'Selesai', ja: '完了', zh: '完成', ko: '완료', hi: 'पूर्ण', ar: 'تم' } },
    Done:        { color: '#48bb78', label: { vi: 'Xong',     en: 'Done', fr: 'Terminé', es: 'Listo', id: 'Selesai', ja: '完了', zh: '完成', ko: '완료', hi: 'पूर्ण', ar: 'تم' } },
    Applied:     { color: '#48bb78', label: { vi: 'Đã áp dụng', en: 'Applied', fr: 'Appliqué', es: 'Aplicado', id: 'Diterapkan', ja: '適用済み', zh: '已应用', ko: '적용됨', hi: 'लागू', ar: 'تم التطبيق' } },
    AwaitingUser:{ color: '#d69e2e', label: { vi: 'Chờ xử lý', en: 'Awaiting', fr: 'En attente', es: 'Pendiente', id: 'Tertunda', ja: '保留中', zh: '待处理', ko: '보류 중', hi: 'लंबित', ar: 'معلق' } },
    Failed:      { color: '#e53e3e', label: { vi: 'Lỗi',      en: 'Failed', fr: 'Échec', es: 'Error', id: 'Gagal', ja: '失敗', zh: '失败', ko: '실패', hi: 'विफल', ar: 'فشل' } }
};
var ACTIVE_STATUSES = ['Pending', 'Queued', 'Processing', 'Running', 'AwaitingUser'];
var DONE_STATUSES = ['Completed', 'Done', 'Applied'];

const POLL_MS = 5000;
const PAGE_SIZE = 30;

function formatTime(ts) {
    if (!ts) return '';
    let ms;
    if (typeof ts === 'string') ms = new Date(ts).getTime();
    else if (typeof ts === 'number') ms = ts < 1e12 ? ts * 1000 : ts;
    else return '';
    const diff = Math.floor((Date.now() - ms) / 1000);
    if (diff < 60) return AntLocalization.translateText({ vi: 'Vừa xong', en: 'Just now', fr: 'À l\'instant', es: 'Ahora', id: 'Baru saja', ja: 'たった今', zh: '刚刚', ko: '방금', hi: 'अभी-अभी', ar: 'الآن' });
    if (diff < 3600) return Math.floor(diff / 60) + AntLocalization.translateText({ vi: ' phút trước', en: 'm ago', fr: ' min', es: ' min', id: ' mnt lalu', ja: '分前', zh: '分钟前', ko: '분 전', hi: ' मिनट पहले', ar: ' دقيقة' });
    if (diff < 86400) return Math.floor(diff / 3600) + AntLocalization.translateText({ vi: ' giờ trước', en: 'h ago', fr: ' h', es: ' h', id: ' jam lalu', ja: '時間前', zh: '小时前', ko: '시간 전', hi: ' घंटे पहले', ar: ' ساعة' });
    return Math.floor(diff / 86400) + AntLocalization.translateText({ vi: ' ngày trước', en: 'd ago', fr: ' j', es: ' d', id: ' hari lalu', ja: '日前', zh: '天前', ko: '일 전', hi: ' दिन पहले', ar: ' يوم' });
}

function jobTypeLabel(type) {
    const map = {
        generate_image: { vi: 'Tạo ảnh', en: 'Generate image', fr: 'Créer une image', es: 'Generar imagen', id: 'Buat gambar', ja: '画像生成', zh: '生成图片', ko: '이미지 생성', hi: 'छवि बनाएँ', ar: 'إنشاء صورة' },
        extract_voucher: { vi: 'Đọc chứng từ', en: 'Extract voucher', fr: 'Lire le document', es: 'Leer documento', id: 'Baca dokumen', ja: '伝票読取', zh: '识别单据', ko: '전표 인식', hi: 'दस्तावेज़ पढ़ें', ar: 'قراءة المستند' },
        generate_content: { vi: 'Tạo nội dung', en: 'Generate content', fr: 'Générer du contenu', es: 'Generar contenido', id: 'Buat konten', ja: 'コンテンツ生成', zh: '生成内容', ko: '콘텐츠 생성', hi: 'सामग्री बनाएँ', ar: 'إنشاء محتوى' },
        generate_translation: { vi: 'Dịch', en: 'Translate', fr: 'Traduire', es: 'Traducir', id: 'Terjemah', ja: '翻訳', zh: '翻译', ko: '번역', hi: 'अनुवाद', ar: 'ترجمة' }
    };
    return AntLocalization.translateText(map[type] || { vi: type, en: type });
}

export default function AIJobsPanel() {
    const api = useMemo(function () { return new AntAPI('ai', 'AIJobQueue'); }, []);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all|active|completed|failed

    const fetchJobs = useCallback(function () {
        const body = {
            pageSize: PAGE_SIZE,
            pageIndex: 1,
            orderBy: 'created_at',
            orderType: 'desc',
            isSearching: false
        };
        // AntRoute auto-mount endpoint list là '/list' → actionGetAll (KHÔNG có '/getAll').
        api.post('/' + api.routeURL + '/list', body, function (result) {
            setLoading(false);
            if (!result || result.error) return;
            // actionGetAll trả { rows, total } (bọc qua AntRoute → result.data.rows).
            // Đọc linh hoạt: result.data.rows | result.data (mảng) | result.rows.
            var d = result.data;
            var rows = Array.isArray(d) ? d
                : (d && Array.isArray(d.rows)) ? d.rows
                : Array.isArray(result.rows) ? result.rows
                : null;
            if (rows) setJobs(rows);
        });
    }, [api]);

    useEffect(function () {
        fetchJobs();
        const t = setInterval(fetchJobs, POLL_MS);
        return function () { clearInterval(t); };
    }, [fetchJobs]);

    const filtered = useMemo(function () {
        if (filter === 'active') return jobs.filter(function (j) { return ACTIVE_STATUSES.indexOf(j.job_status) >= 0; });
        if (filter === 'completed') return jobs.filter(function (j) { return DONE_STATUSES.indexOf(j.job_status) >= 0; });
        if (filter === 'failed') return jobs.filter(function (j) { return j.job_status === 'Failed'; });
        return jobs;
    }, [jobs, filter]);

    const counts = useMemo(function () {
        const c = { all: jobs.length, active: 0, completed: 0, failed: 0 };
        jobs.forEach(function (j) {
            if (ACTIVE_STATUSES.indexOf(j.job_status) >= 0) c.active++;
            else if (DONE_STATUSES.indexOf(j.job_status) >= 0) c.completed++;
            else if (j.job_status === 'Failed') c.failed++;
        });
        return c;
    }, [jobs]);

    return (
        <div className="aijobs-panel">
            <div className="aijobs-header">
                <span className="aijobs-title">
                    {AntLocalization.translateText({ vi: 'AI Jobs', en: 'AI Jobs', fr: 'Tâches IA', es: 'Tareas IA', id: 'Tugas AI', ja: 'AIジョブ', zh: 'AI任务', ko: 'AI 작업', hi: 'AI कार्य', ar: 'مهام AI' })}
                </span>
                <button className="aijobs-refresh" type="button" onClick={fetchJobs} title="Refresh">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 12a9 9 0 0115-6.7L21 8M21 3v5h-5M21 12a9 9 0 01-15 6.7L3 16M3 21v-5h5" />
                    </svg>
                </button>
            </div>

            <div className="aijobs-filters">
                {['all', 'active', 'completed', 'failed'].map(function (k) {
                    const label = AntLocalization.translateText({
                        all: { vi: 'Tất cả', en: 'All', fr: 'Tout', es: 'Todo', id: 'Semua', ja: 'すべて', zh: '全部', ko: '전체', hi: 'सभी', ar: 'الكل' },
                        active: { vi: 'Đang chạy', en: 'Active', fr: 'En cours', es: 'En curso', id: 'Berjalan', ja: '実行中', zh: '运行中', ko: '실행 중', hi: 'चल रहा है', ar: 'قيد التشغيل' },
                        completed: { vi: 'Xong', en: 'Done', fr: 'Terminé', es: 'Listo', id: 'Selesai', ja: '完了', zh: '完成', ko: '완료', hi: 'पूर्ण', ar: 'تم' },
                        failed: { vi: 'Lỗi', en: 'Failed', fr: 'Échec', es: 'Error', id: 'Gagal', ja: '失敗', zh: '失败', ko: '실패', hi: 'विफल', ar: 'فشل' }
                    }[k]);
                    return (
                        <button
                            key={k}
                            type="button"
                            className={'aijobs-filter' + (filter === k ? ' is-active' : '')}
                            onClick={function () { setFilter(k); }}
                        >
                            {label} <span className="aijobs-filter-count">({counts[k]})</span>
                        </button>
                    );
                })}
            </div>

            <div className="aijobs-list">
                {loading && <div className="aijobs-empty">{AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読込中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोडिंग...', ar: 'تحميل...' })}</div>}
                {!loading && filtered.length === 0 && (
                    <div className="aijobs-empty">{AntLocalization.translateText({ vi: 'Chưa có job nào', en: 'No jobs yet', fr: 'Aucune tâche', es: 'Sin tareas', id: 'Belum ada tugas', ja: 'ジョブなし', zh: '暂无任务', ko: '작업 없음', hi: 'कोई कार्य नहीं', ar: 'لا توجد مهام' })}</div>
                )}
                {filtered.map(function (j) {
                    const meta = STATUS_META[j.job_status] || { color: '#cbd5e0', label: { vi: j.job_status, en: j.job_status } };
                    return (
                        <div key={j.id || j.code} className="aijobs-item">
                            <div className="aijobs-item-head">
                                <span className="aijobs-badge" style={{ background: meta.color }}>
                                    {AntLocalization.translateText(meta.label)}
                                </span>
                                <span className="aijobs-item-type">{jobTypeLabel(j.job_type)}</span>
                            </div>
                            <div className="aijobs-item-meta">
                                {j.result_ref_code ? <span className="aijobs-item-code">{j.result_ref_code}</span> : null}
                                {j.provider ? <span className="aijobs-item-provider">· {j.provider}/{j.model}</span> : null}
                                {(j.retry_count || 0) > 0 ? <span className="aijobs-item-retry">· retry {j.retry_count}</span> : null}
                            </div>
                            <div className="aijobs-item-foot">
                                <span className="aijobs-item-time">{formatTime(j.created_at)}</span>
                                {j.job_status === 'Failed' && j.error_message ? (
                                    <span className="aijobs-item-error" title={j.error_message}>{String(j.error_message).substring(0, 50)}</span>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
